import React, { useEffect, useMemo, useState } from 'react';
import { Alert, AutoComplete, Checkbox, Input, Modal } from 'antd';

import FormExposure from './FormExposure';
import { setPublicFormAddress } from '../../services/publicForms';
import { normalizeId } from '../../utils/ids';
import { addressUrl, describePublicFormError, formExposure, slugProblem, suggestSlug } from '../../utils/publicForms';

/**
 * Point an address at this form (KPR-513; ruling f02: "Pausing keeps the
 * address, and it can be re-pointed to another form"). One door for three
 * things, as the API's `PUT …/address` is:
 *   - a NEW address for a form that has none (a draft, or one whose address
 *     moved away) — a draft goes live here, so the manager says they know
 *     anyone with it can send a submission;
 *   - ANOTHER FORM'S address moved here — the sign on the lamp post now opens
 *     this form, and the form it named is left with no address (said before
 *     Save, by name);
 *   - an address that points at nothing (its form was retired), typed by its
 *     slug: the list cannot name those, because nothing holds them.
 * A form has one address: giving it another lets its present one go.
 *
 * A DRAFT going live (a package's prepared form among them, keepr-api
 * KPR-360) first says what it will expose, in plain words — what it asks,
 * privately, shows, fills in, takes as files, and that the address never
 * expires (FormExposure) — above the box that says the manager knows.
 * `cardName` and `labelOf` name the card and its elements as it labels them.
 */
export default function PublicFormAddressModal({
	open,
	onClose,
	onSaved,
	collectionId,
	form,
	forms = [],
	collectionSlug = null,
	suggestedCollectionSlug = null,
	cardName = null,
	labelOf = name => name,
}) {
	const [slug, setSlug] = useState('');
	const [newCollectionSlug, setNewCollectionSlug] = useState('');
	const [acknowledge, setAcknowledge] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const formId = normalizeId(form?._id);
	const isDraft = form?.state === 'draft';

	useEffect(() => {
		if (!open) return;
		setSlug(form?.address ? '' : (form?.suggestedSlug || suggestSlug(form?.name)));
		setNewCollectionSlug(suggestedCollectionSlug || '');
		setAcknowledge(false);
		setSaving(false);
		setError(null);
	}, [open, form, suggestedCollectionSlug]);

	// Every OTHER form's address, offered by name: the usual re-point.
	const others = useMemo(() => forms
		.filter(other => other?.address?.slug && normalizeId(other._id) !== formId)
		.map(other => ({ slug: other.address.slug, form: other })), [forms, formId]);
	const options = useMemo(() => others.map(({ slug: value, form: other }) => ({
		value,
		label: (
			<span className="public-form-address__option">
				<span>{value}</span>
				<span className="intake-link-modal__reason">{`Now opens ${other.name || 'another form'}`}</span>
			</span>
		),
	})), [others]);
	const typed = slug.trim();
	const taking = others.find(other => other.slug === typed) || null;
	const needsCollectionSlug = !collectionSlug;
	const chosenCollectionSlug = needsCollectionSlug ? newCollectionSlug.trim() : collectionSlug;
	const problem = (needsCollectionSlug ? slugProblem(newCollectionSlug, 'The collection’s address') : null)
		|| slugProblem(typed, 'The address')
		|| (typed && typed === form?.address?.slug ? 'This form is already at that address.' : null)
		|| (isDraft && !acknowledge ? 'Tick the box to say you know anyone with the address can send a submission.' : null);

	const handleOk = async () => {
		if (problem || !formId) return;
		setSaving(true);
		setError(null);
		try {
			const saved = await setPublicFormAddress(collectionId, formId, {
				slug: typed,
				collectionSlug: needsCollectionSlug ? newCollectionSlug : undefined,
				acknowledge: isDraft ? acknowledge : undefined,
			});
			onSaved?.(saved, { movedFrom: taking?.form || null });
		} catch (err) {
			setError(describePublicFormError(err, {}, 'The address could not be changed.'));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Modal
			open={open}
			title="Point an address here"
			okText={taking ? 'Move address here' : 'Use this address'}
			okButtonProps={{ disabled: Boolean(problem), 'data-action': 'public-form-address-save' }}
			confirmLoading={saving}
			onOk={handleOk}
			onCancel={onClose}
			destroyOnClose
			width={520}
			className="share-links__modal public-form-address"
		>
			<div className="modal-identity">
				<div className="micro-label">Public form</div>
				<div className="item-share-modal__preview-title">{form?.name || 'Public form'}</div>
			</div>
			<p className="share-links__hint">
				{form?.address
					? `Now at ${addressUrl(form.address)}. Giving it another address lets that one go: it will point at nothing until you point it at a form.`
					: 'This form has no address, so nobody can reach it.'}
			</p>

			{needsCollectionSlug ? (
				<div className="share-links__field">
					<label className="share-links__field-label" htmlFor="public-form-address-collection">This collection</label>
					<Input
						id="public-form-address-collection"
						maxLength={48}
						value={newCollectionSlug}
						onChange={event => setNewCollectionSlug(event.target.value.toLowerCase())}
					/>
					<p className="share-links__hint">Chosen once, for every form it will ever have.</p>
				</div>
			) : null}

			<div className="share-links__field">
				<label className="share-links__field-label" htmlFor="public-form-address-slug">Address</label>
				<AutoComplete
					id="public-form-address-slug"
					className="public-form-address__input"
					value={slug}
					options={options}
					onChange={value => setSlug(`${value || ''}`.toLowerCase())}
					filterOption={(input, option) => `${option?.value || ''}`.includes(`${input || ''}`.toLowerCase())}
					placeholder="Another form’s address, or a new one"
				/>
				<p className="public-form-modal__preview" data-testid="public-form-address-preview">
					{addressUrl({ path: `/f/${chosenCollectionSlug || '…'}/${typed || '…'}` })}
				</p>
				{taking ? (
					<Alert
						className="public-form-address__moving"
						type="warning"
						showIcon
						message={`This address now opens ${taking.form.name || 'another form'}. Moving it here leaves that form with no address.`}
						data-testid="public-form-address-moving"
					/>
				) : (
					<p className="share-links__hint">An address a retired form used to have can be pointed here by typing it.</p>
				)}
			</div>

			{isDraft ? (
				<div className="share-links__field">
					<span className="share-links__field-label">What it will take</span>
					<FormExposure lines={formExposure(form, { cardName, labelOf })} />
					<Checkbox checked={acknowledge} onChange={event => setAcknowledge(event.target.checked)}>
						I understand that anyone with the address can send this collection a submission.
					</Checkbox>
				</div>
			) : null}

			{typed && problem ? <p className="share-links__hint intake-link-modal__problem">{problem}</p> : null}
			{error ? <Alert className="intake-link-modal__error" type="error" showIcon message={error} data-testid="public-form-address-error" /> : null}
		</Modal>
	);
}
